import React, { useState, useEffect, useCallback } from 'react';
import { collection, getDocs, query, where } from 'firebase/firestore';
import { db } from '../services/firebase';
import { getSeekerProfile } from '../services/seekerProfileService';
import LoadingSpinner from '../components/LoadingSpinner';

// Filter Sidebar Component (outside main component to prevent re-renders)
const FilterSidebar = ({ filters, updateFilter, onClearFilters }) => (
    <div className="space-y-6">
        <div>
            <h2 className="text-xs font-extrabold text-slate-900 uppercase tracking-widest mb-5 flex items-center gap-2">
                <div className="w-8 h-8 bg-primary-100 rounded-lg flex items-center justify-center text-primary-600">
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M3 4a1 1 0 011-1h16a1 1 0 011 1v2.586a1 1 0 01-.293.707l-6.414 6.414a1 1 0 00-.293.707V17l-4 4v-6.586a1 1 0 00-.293-.707L3.293 7.293A1 1 0 013 6.586V4z" />
                    </svg>
                </div>
                <span>Advanced Filter Criteria</span>
            </h2>
            <div className="space-y-4">
                {/* Search Keywords */}
                <div>
                    <label className="block text-[10px] font-extrabold text-slate-500 uppercase tracking-widest mb-1.5">Competence & Title Search</label>
                    <div className="relative">
                        <input
                            type="text"
                            placeholder="Enter keywords, roles, or target skillsets..."
                            value={filters.search}
                            onChange={(e) => updateFilter('search', e.target.value)}
                            className="w-full pl-10 pr-3 py-2.5 border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-primary-600 focus:border-transparent text-sm bg-slate-50/50 font-semibold text-slate-700"
                        />
                        <svg className="w-4 h-4 text-slate-400 absolute left-3.5 top-1/2 -translate-y-1/2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                        </svg>
                    </div>
                </div>

                {/* Experience Level */}
                <div>
                    <label className="block text-[10px] font-extrabold text-slate-500 uppercase tracking-widest mb-1.5">Work Experience Duration</label>
                    <select
                        value={filters.experience}
                        onChange={(e) => updateFilter('experience', e.target.value)}
                        className="w-full px-3 py-2.5 border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-primary-600 focus:border-transparent bg-slate-50/50 text-sm font-semibold text-slate-700"
                    >
                        <option value="any">Unspecified Tenure</option>
                        <option value="0-1">0-1 Years</option>
                        <option value="1-3">1-3 Years</option>
                        <option value="3-5">3-5 Years</option>
                        <option value="5-10">5-10 Years</option>
                        <option value="10+">10+ Years</option>
                    </select>
                </div>

                {/* Location */}
                <div>
                    <label className="block text-[10px] font-extrabold text-slate-500 uppercase tracking-widest mb-1.5">Preferred Geography</label>
                    <input
                        type="text"
                        placeholder="Filter by city, state, or area..."
                        value={filters.location}
                        onChange={(e) => updateFilter('location', e.target.value)}
                        className="w-full px-3 py-2.5 border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-primary-600 focus:border-transparent text-sm bg-slate-50/50 font-semibold text-slate-700"
                    />
                </div>

                {/* Salary Range */}
                <div>
                    <label className="block text-[10px] font-extrabold text-slate-500 uppercase tracking-widest mb-1.5">Expected Annual Package</label>
                    <div className="grid grid-cols-2 gap-2">
                        <input
                            type="number"
                            placeholder="Minimum INR"
                            value={filters.minSalary}
                            onChange={(e) => updateFilter('minSalary', e.target.value)}
                            className="w-full px-3 py-2.5 border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-primary-600 focus:border-transparent text-sm bg-slate-50/50 font-semibold text-slate-700"
                        />
                        <input
                            type="number"
                            placeholder="Maximum INR"
                            value={filters.maxSalary}
                            onChange={(e) => updateFilter('maxSalary', e.target.value)}
                            className="w-full px-3 py-2.5 border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-primary-600 focus:border-transparent text-sm bg-slate-50/50 font-semibold text-slate-700"
                        />
                    </div>
                </div>

                {/* Skills */}
                <div>
                    <label className="block text-[10px] font-extrabold text-slate-500 uppercase tracking-widest mb-1.5">Technical Skillsets</label>
                    <input
                        type="text"
                        placeholder="Specify skills (comma separated)..."
                        value={filters.skills}
                        onChange={(e) => updateFilter('skills', e.target.value)}
                        className="w-full px-3 py-2.5 border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-primary-600 focus:border-transparent text-sm bg-slate-50/50 font-semibold text-slate-700"
                    />
                </div>

                {/* Education */}
                <div>
                    <label className="block text-[10px] font-extrabold text-slate-500 uppercase tracking-widest mb-1.5">Degree Qualifications</label>
                    <input
                        type="text"
                        placeholder="Search by degrees or institutions..."
                        value={filters.education}
                        onChange={(e) => updateFilter('education', e.target.value)}
                        className="w-full px-3 py-2.5 border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-primary-600 focus:border-transparent text-sm bg-slate-50/50 font-semibold text-slate-700"
                    />
                </div>

                {/* Notice Period */}
                <div>
                    <label className="block text-[10px] font-extrabold text-slate-500 uppercase tracking-widest mb-1.5">Joining Availability</label>
                    <select
                        value={filters.noticePeriod}
                        onChange={(e) => updateFilter('noticePeriod', e.target.value)}
                        className="w-full px-3 py-2.5 border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-primary-600 focus:border-transparent bg-slate-50/50 text-sm font-semibold text-slate-700"
                    >
                        <option value="any">Any</option>
                        <option value="immediate">Immediate</option>
                        <option value="15">15 Days or less</option>
                        <option value="30">30 Days or less</option>
                        <option value="60">60 Days or less</option>
                        <option value="90+">90+ Days</option>
                    </select>
                </div>
            </div>

            {/* Clear Filters */}
            <button
                onClick={onClearFilters}
                className="w-full mt-6 py-2.5 bg-gradient-to-r from-primary-600 to-primary-850 text-white font-bold text-xs rounded-xl shadow-md transition-all duration-200"
            >
                Clear All Search Criteria
            </button>
        </div>
    </div>
);

const SeekerDatabase = ({ user, userData }) => {
    const [seekers, setSeekers] = useState([]);
    const [loading, setLoading] = useState(true);
    const [currentPage, setCurrentPage] = useState(1);
    const seekersPerPage = 20;
    const isSuperAdmin = userData?.userType === 'superadmin';

    // Filter State - Only fields shown in seeker profile
    const [filters, setFilters] = useState({
        search: '',
        experience: 'any',
        location: '',
        minSalary: '',
        maxSalary: '',
        education: '',
        skills: '',
        noticePeriod: 'any'
    });

    const [showMobileFilters, setShowMobileFilters] = useState(false);
    const [unlockedContacts, setUnlockedContacts] = useState(new Set());

    useEffect(() => {
        const fetchSeekers = async () => {
            try {
                // Super Admin: Fetch ALL job seekers
                if (isSuperAdmin) {
                    const q = query(collection(db, 'users'), where('userType', '==', 'jobseeker'));
                    const snapshot = await getDocs(q);
                    const seekersData = await Promise.all(
                        snapshot.docs.map(async (doc) => {
                            const userData = { id: doc.id, ...doc.data() };

                            // Fetch seeker profile
                            try {
                                const result = await getSeekerProfile(userData.id);
                                if (result.success) {
                                    userData.seekerProfile = result.profile;
                                }
                            } catch (err) {
                                console.error(`Error fetching seeker profile for ${userData.id}:`, err);
                            }

                            return userData;
                        })
                    );
                    setSeekers(seekersData);
                    setLoading(false);
                    return;
                }

                // Employer: Fetch only applicants who applied to their jobs
                // Step 1: Get all jobs posted by this employer
                const jobsQuery = query(
                    collection(db, 'jobs'),
                    where('userId', '==', user.uid)
                );
                const jobsSnapshot = await getDocs(jobsQuery);
                const employerJobIds = jobsSnapshot.docs.map(doc => doc.id);

                if (employerJobIds.length === 0) {
                    // No jobs posted, no applicants to show
                    setSeekers([]);
                    setLoading(false);
                    return;
                }

                // Step 2: Get all applications for these jobs
                // Firestore 'in' operator has a limit of 10 items, so we need to batch
                const batchSize = 10;
                const batches = [];
                for (let i = 0; i < employerJobIds.length; i += batchSize) {
                    batches.push(employerJobIds.slice(i, i + batchSize));
                }

                const allApplications = [];
                for (const batch of batches) {
                    const applicationsQuery = query(
                        collection(db, 'applications'),
                        where('jobId', 'in', batch)
                    );
                    const applicationsSnapshot = await getDocs(applicationsQuery);
                    allApplications.push(...applicationsSnapshot.docs);
                }

                // Get unique applicant IDs
                const applicantIds = [...new Set(allApplications.map(doc => doc.data().applicantId))];

                if (applicantIds.length === 0) {
                    // No applications yet
                    setSeekers([]);
                    setLoading(false);
                    return;
                }

                // Step 3: Fetch seeker data for applicants only
                const seekersData = await Promise.all(
                    applicantIds.map(async (applicantId) => {
                        try {
                            // Get user data
                            const userQuery = query(
                                collection(db, 'users'),
                                where('__name__', '==', applicantId)
                            );
                            const userSnapshot = await getDocs(userQuery);

                            if (userSnapshot.empty) return null;

                            const userDoc = userSnapshot.docs[0];
                            const userData = { id: userDoc.id, ...userDoc.data() };

                            // Fetch seeker profile
                            try {
                                const result = await getSeekerProfile(userData.id);
                                if (result.success) {
                                    userData.seekerProfile = result.profile;
                                }
                            } catch (err) {
                                console.error(`Error fetching seeker profile for ${userData.id}:`, err);
                            }

                            return userData;
                        } catch (err) {
                            console.error(`Error fetching user ${applicantId}:`, err);
                            return null;
                        }
                    })
                );

                // Filter out null values
                const validSeekers = seekersData.filter(seeker => seeker !== null);
                setSeekers(validSeekers);
            } catch (error) {
                console.error('Error fetching seekers:', error);
            } finally {
                setLoading(false);
            }
        };

        if (user?.uid) {
            fetchSeekers();
        }
    }, [user?.uid, isSuperAdmin]);

    // Filter Logic
    const filteredSeekers = seekers.filter(seeker => {
        const profile = seeker.seekerProfile || {};
        const personal = profile.personalInfo || {};
        const exp = profile.experience || [];
        const edu = profile.education || [];
        const prefs = profile.preferences || {};
        const skills = profile.skills || [];

        // Search (Global) - Name, Skills, Role, Location, Company
        if (filters.search) {
            const searchLower = filters.search.toLowerCase();
            const nameMatch = `${seeker.firstName} ${seeker.lastName}`.toLowerCase().includes(searchLower);
            const skillMatch = skills.some(s => s.toLowerCase().includes(searchLower));
            const roleMatch = exp.some(e => e.jobTitle?.toLowerCase().includes(searchLower));
            const locMatch = personal.location?.toLowerCase().includes(searchLower);
            const companyMatch = exp.some(e => e.company?.toLowerCase().includes(searchLower));

            if (!nameMatch && !skillMatch && !roleMatch && !locMatch && !companyMatch) return false;
        }

        // Experience Level
        if (filters.experience !== 'any') {
            const totalYears = exp.reduce((acc, curr) => acc + (parseInt(curr.yearsOfExperience) || 0), 0);
            if (filters.experience === '0-1' && totalYears > 1) return false;
            if (filters.experience === '1-3' && (totalYears < 1 || totalYears > 3)) return false;
            if (filters.experience === '3-5' && (totalYears < 3 || totalYears > 5)) return false;
            if (filters.experience === '5-10' && (totalYears < 5 || totalYears > 10)) return false;
            if (filters.experience === '10+' && totalYears < 10) return false;
        }

        // Expected Salary Range
        if (filters.minSalary) {
            const candidateSalary = prefs.expectedSalary?.min || prefs.expectedSalary?.max || 0;
            if (parseInt(candidateSalary) < parseInt(filters.minSalary)) return false;
        }
        if (filters.maxSalary) {
            const candidateSalary = prefs.expectedSalary?.max || prefs.expectedSalary?.min || 999999999;
            if (parseInt(candidateSalary) > parseInt(filters.maxSalary)) return false;
        }

        // Preferred Work Locations
        if (filters.location) {
            const locationLower = filters.location.toLowerCase();
            const currentLocMatch = personal.location?.toLowerCase().includes(locationLower);
            const preferredLocMatch = prefs.preferredLocations && Array.isArray(prefs.preferredLocations) &&
                prefs.preferredLocations.some(loc => loc.toLowerCase().includes(locationLower));

            if (!currentLocMatch && !preferredLocMatch) return false;
        }

        // Education Qualification
        if (filters.education) {
            const eduLower = filters.education.toLowerCase();
            const eduMatch = edu.some(e =>
                e.degree?.toLowerCase().includes(eduLower) ||
                e.fieldOfStudy?.toLowerCase().includes(eduLower) ||
                e.institution?.toLowerCase().includes(eduLower)
            );
            if (!eduMatch) return false;
        }

        // Skills
        if (filters.skills) {
            const skillQuery = filters.skills.toLowerCase();
            if (!skills.some(s => s.toLowerCase().includes(skillQuery))) return false;
        }

        // Notice Period (Simplified)
        if (filters.noticePeriod !== 'any') {
            const noticePeriod = prefs.noticePeriod || personal.noticePeriod || 'not-specified';
            if (filters.noticePeriod === 'immediate' && noticePeriod !== 'immediate' && noticePeriod !== '0' && noticePeriod !== '15') return false;
            if (filters.noticePeriod === '15' && !['15', '30', 'immediate', '0'].includes(noticePeriod)) return false;
            if (filters.noticePeriod === '30' && !['30', '15', 'immediate', '0'].includes(noticePeriod)) return false;
            if (filters.noticePeriod === '60' && !['60', '30', '15', 'immediate', '0'].includes(noticePeriod)) return false;
            if (filters.noticePeriod === '90+' && noticePeriod === '90+') return false;
        }

        return true;
    });

    // Reset to first page when any filter changes
    useEffect(() => {
        setCurrentPage(1);
    }, [JSON.stringify(filters)]);

    const handleUnlock = (id) => {
        setUnlockedContacts(prev => new Set(prev).add(id));
    };

    // Text Highlighting Helper - Only highlight when search is active
    const HighlightText = ({ text, highlight }) => {
        if (!text) return null;
        if (!highlight || highlight.trim() === '') return <span>{text}</span>;

        try {
            const escaped = highlight.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
            const parts = text.toString().split(new RegExp(`(${escaped})`, 'gi'));
            return (
                <span>
                    {parts.map((part, i) =>
                        part.toLowerCase() === highlight.toLowerCase() ?
                            <span key={i} className="bg-amber-200 text-slate-900 font-bold rounded px-0.5">{part}</span> : part
                    )}
                </span>
            );
        } catch (e) {
            return <span>{text}</span>;
        }
    };

    // Pagination
    const indexOfLastSeeker = currentPage * seekersPerPage;
    const indexOfFirstSeeker = indexOfLastSeeker - seekersPerPage;
    const currentSeekers = filteredSeekers.slice(indexOfFirstSeeker, indexOfLastSeeker);
    const totalPages = Math.ceil(filteredSeekers.length / seekersPerPage);

    const handlePageChange = (pageNumber) => {
        setCurrentPage(pageNumber);
        const listTop = document.getElementById('seeker-list-top');
        if (listTop) listTop.scrollIntoView({ behavior: 'smooth' });
    };

    // Update filter handler
    const updateFilter = useCallback((key, value) => {
        setFilters(prev => ({ ...prev, [key]: value }));
    }, []);

    // Clear filters handler
    const handleClearFilters = useCallback(() => {
        setFilters({
            search: '',
            experience: 'any',
            minSalary: '',
            maxSalary: '',
            location: '',
            education: '',
            skills: '',
            noticePeriod: 'any'
        });
    }, []);

    if (loading) return <LoadingSpinner />;

    return (
        <div className="min-h-screen bg-slate-50/50 relative overflow-hidden pb-24 lg:pb-0">
            {/* Background grid pattern */}
            <div className="absolute inset-0 -z-10 bg-[radial-gradient(#0e3a61_1px,transparent_1px)] [background-size:24px_24px] opacity-5"></div>

            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 relative z-10">
                
                {/* Header Banner */}
                <div className="bg-gradient-to-br from-[#0e3a61] to-[#051728] rounded-[2rem] rounded-tr-[4rem] rounded-bl-[4rem] shadow-xl border border-[#0e3a61]/10 overflow-hidden relative p-8 mb-8">
                    <div className="absolute top-0 right-0 w-64 h-64 bg-white/5 rounded-full -translate-y-20 translate-x-20 pointer-events-none"></div>
                    <div className="absolute -bottom-10 -left-10 w-48 h-48 bg-[#e2822f]/10 rounded-full blur-2xl pointer-events-none"></div>
                    
                    <div className="relative flex flex-col md:flex-row justify-between items-start md:items-center gap-6 z-10 text-white">
                        <div className="flex-1 min-w-0">
                            <span className="text-[10px] uppercase font-bold tracking-widest text-[#e2822f] bg-[#e2822f]/15 px-3 py-1 rounded-full border border-[#e2822f]/20">
                                Verified Candidate Database
                            </span>
                            <h1 className="text-2xl sm:text-3xl font-extrabold mt-3 tracking-tight">
                                Candidate Sourcing Engine
                            </h1>
                            <p className="mt-2 text-slate-300 text-xs sm:text-sm max-w-xl leading-relaxed">
                                Browse verified applicant profiles, evaluate skill alignment, and unlock direct contact details.
                            </p>
                        </div>
                        <div className="flex items-center gap-3 bg-white/10 rounded-2xl p-4 border border-white/15">
                            <div className="w-10 h-10 bg-[#e2822f] rounded-xl flex items-center justify-center text-white shadow-lg">
                                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                                </svg>
                            </div>
                            <div className="text-left">
                                <p className="text-[10px] uppercase font-bold text-slate-300">Matching Profiles</p>
                                <p className="text-xl font-black text-white">{filteredSeekers.length}</p>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Split Grid Layout */}
                <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-start">
                    
                    {/* Desktop Filter Sidebar Card */}
                    <div className="hidden lg:block lg:col-span-4 bg-white rounded-[2rem] shadow-xl p-6 border border-slate-100">
                        <FilterSidebar filters={filters} updateFilter={updateFilter} onClearFilters={handleClearFilters} />
                    </div>

                    {/* List / Grid Column */}
                    <div className="lg:col-span-8 space-y-6" id="seeker-list-top">
                        
                        {/* Results Summary Bar */}
                        <div className="bg-white rounded-[2rem] shadow-xl border border-slate-100 p-4">
                            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                                <div className="flex items-center gap-3">
                                    <div className="w-9 h-9 bg-[#0e3a61]/5 rounded-xl flex items-center justify-center text-[#0e3a61]">
                                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                                        </svg>
                                    </div>
                                    <div>
                                        <p className="text-[10px] font-bold uppercase tracking-wider text-slate-500">Search Results</p>
                                        <p className="text-sm font-extrabold text-slate-900">{filteredSeekers.length} Profiles Discovered</p>
                                    </div>
                                </div>
                                <div className="flex items-center gap-3 text-xs font-bold text-slate-500">
                                    <span>Page {currentPage} of {totalPages || 1}</span>
                                    {totalPages > 1 && (
                                        <div className="flex items-center gap-1">
                                            <button
                                                onClick={() => handlePageChange(currentPage - 1)}
                                                disabled={currentPage === 1}
                                                className="p-1.5 rounded-lg text-slate-500 hover:bg-slate-50 border border-slate-100 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                                            >
                                                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M15 19l-7-7 7-7" />
                                                </svg>
                                            </button>
                                            <button
                                                onClick={() => handlePageChange(currentPage + 1)}
                                                disabled={currentPage === totalPages}
                                                className="p-1.5 rounded-lg text-slate-500 hover:bg-slate-50 border border-slate-100 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                                            >
                                                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M9 5l7 7-7 7" />
                                                </svg>
                                            </button>
                                        </div>
                                    )}
                                </div>
                            </div>
                        </div>

                        {/* Candidates List */}
                        <div className="space-y-6">
                            {currentSeekers.map((seeker) => {
                                const profile = seeker.seekerProfile || {};
                                const experience = profile.experience || [];
                                const currentJob = experience.find(e => e.isCurrentJob) || experience[0] || {};
                                const education = profile.education?.[0] || {};
                                const personal = profile.personalInfo || {};
                                const preferences = profile.preferences || {};
                                const skills = profile.skills || [];
                                const isUnlocked = isSuperAdmin || unlockedContacts.has(seeker.id);

                                return (
                                    <div
                                        key={seeker.id}
                                        className="bg-white rounded-[2rem] rounded-tr-[3.5rem] rounded-bl-[3.5rem] border border-slate-100 p-6 hover:shadow-2xl hover:-translate-y-1 transition-all duration-300 shadow-md relative overflow-hidden group hover:border-[#0e3a61]/20"
                                    >
                                        {/* Background hover pattern */}
                                        <div className="absolute top-0 right-0 w-32 h-32 bg-gradient-to-br from-[#e2822f]/5 to-[#0e3a61]/5 rounded-full -translate-y-16 translate-x-16 opacity-0 group-hover:opacity-100 transition-opacity duration-300 pointer-events-none"></div>

                                        <div className="flex flex-col sm:flex-row gap-5 relative z-10">
                                            {/* Avatar Box */}
                                            <div className="flex-shrink-0">
                                                <div className="w-14 h-14 bg-gradient-to-br from-[#0e3a61] to-[#08233c] text-white rounded-2xl flex items-center justify-center font-extrabold text-xl shadow-md shadow-blue-900/10">
                                                    {seeker.firstName?.charAt(0)?.toUpperCase()}
                                                </div>
                                            </div>

                                            {/* Profile Main Content */}
                                            <div className="flex-1 min-w-0">
                                                {/* Header Row */}
                                                <div className="flex flex-col sm:flex-row justify-between items-start gap-4 mb-4">
                                                    <div>
                                                        <h3 className="text-lg font-extrabold text-slate-900 mb-1">
                                                            {filters.search ? (
                                                                <HighlightText text={`${seeker.firstName} ${seeker.lastName}`} highlight={filters.search} />
                                                            ) : (
                                                                `${seeker.firstName} ${seeker.lastName}`
                                                            )}
                                                        </h3>

                                                        {/* Location, Tenure, Salary Indicators */}
                                                        <div className="flex flex-wrap items-center gap-x-4 gap-y-2 text-xs font-semibold text-slate-500">
                                                            <div className="flex items-center gap-1">
                                                                <svg className="w-4 h-4 text-slate-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 13.255A23.931 23.931 0 0112 15c-3.183 0-6.22-.62-9-1.745M16 6V4a2 2 0 00-2-2h-4a2 2 0 00-2 2v2m4 6h.01M5 20h14a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                                                                </svg>
                                                                <span>{experience.length ? `${experience.reduce((acc, curr) => acc + (parseInt(curr.yearsOfExperience) || 0), 0)} Years Tenure` : 'Fresher'}</span>
                                                            </div>
                                                            <div className="flex items-center gap-1">
                                                                <svg className="w-4 h-4 text-slate-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1" />
                                                                </svg>
                                                                <span>{preferences.expectedSalary?.min ? `₹${(preferences.expectedSalary.min / 100000).toFixed(1)} Lakh` : 'Not Disclosed'}</span>
                                                            </div>
                                                            <div className="flex items-center gap-1">
                                                                <svg className="w-4 h-4 text-slate-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                                                                </svg>
                                                                {filters.search || filters.location ? (
                                                                    <HighlightText text={personal.location || `${personal.taluka ? `${personal.taluka}, ` : ''}${personal.district ? `${personal.district}, ` : ''}${personal.state || 'Not specified'}`} highlight={filters.location || filters.search} />
                                                                ) : (
                                                                    personal.location || `${personal.taluka ? `${personal.taluka}, ` : ''}${personal.district ? `${personal.district}, ` : ''}${personal.state || 'Not specified'}`
                                                                )}
                                                            </div>
                                                        </div>
                                                    </div>

                                                    {/* Action / Contact Block */}
                                                    <div>
                                                        {!isUnlocked ? (
                                                            <button
                                                                onClick={() => handleUnlock(seeker.id)}
                                                                className="px-5 py-2.5 bg-gradient-to-r from-[#e2822f] to-[#be6417] text-white font-extrabold text-xs uppercase tracking-wider rounded-xl shadow-lg hover:shadow-xl shadow-orange-500/10 hover:shadow-orange-500/20 hover:-translate-y-0.5 active:translate-y-0 transition-all duration-200"
                                                            >
                                                                Unlock Candidate Details
                                                            </button>
                                                        ) : (
                                                            <div className="bg-[#508d0c]/5 rounded-2xl p-3.5 border-2 border-[#508d0c]/30 shadow-[0_0_15px_rgba(80,141,12,0.15)] flex flex-col gap-2.5 transition-all duration-300">
                                                                <a href={`tel:${personal.phone}`} className="inline-flex items-center gap-2 text-xs font-bold text-[#508d0c] hover:text-[#3f7009] transition-colors">
                                                                    <div className="w-6 h-6 bg-[#508d0c]/10 rounded-lg flex items-center justify-center">
                                                                        <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
                                                                        </svg>
                                                                    </div>
                                                                    <span>{personal.phone}</span>
                                                                </a>
                                                                <a href={`mailto:${personal.email}`} className="inline-flex items-center gap-2 text-xs font-bold text-[#0e3a61] hover:text-[#0b2e4e] transition-colors">
                                                                    <div className="w-6 h-6 bg-[#0e3a61]/10 rounded-lg flex items-center justify-center">
                                                                        <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M3 8l7.89 4.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                                                                        </svg>
                                                                    </div>
                                                                    <span>{personal.email}</span>
                                                                </a>
                                                            </div>
                                                        )}
                                                    </div>
                                                </div>

                                                {/* Preference Badges */}
                                                {((preferences.preferredJobTypes && preferences.preferredJobTypes.length > 0) ||
                                                  (preferences.preferredWorkModes && preferences.preferredWorkModes.length > 0) ||
                                                  preferences.noticePeriod || personal.noticePeriod) && (
                                                    <div className="flex flex-wrap items-center gap-2 mb-4">
                                                        {preferences.preferredJobTypes?.map((type, i) => (
                                                            <span key={i} className="px-2.5 py-1 text-[10px] font-bold uppercase tracking-wider bg-[#0e3a61]/5 text-[#0e3a61] rounded-lg border border-[#0e3a61]/10">
                                                                {type}
                                                            </span>
                                                        ))}
                                                        {preferences.preferredWorkModes?.map((mode, i) => (
                                                            <span key={i} className="px-2.5 py-1 text-[10px] font-bold uppercase tracking-wider bg-[#508d0c]/5 text-[#508d0c] rounded-lg border border-[#508d0c]/10">
                                                                {mode}
                                                            </span>
                                                        ))}
                                                        {(preferences.noticePeriod || personal.noticePeriod) && (
                                                            <span className="px-2.5 py-1 text-[10px] font-bold uppercase tracking-wider bg-orange-50 text-[#e2822f] rounded-lg border border-orange-200/50">
                                                                Notice: {preferences.noticePeriod || personal.noticePeriod}
                                                            </span>
                                                        )}
                                                    </div>
                                                )}

                                                {/* Content Details Block */}
                                                <div className="space-y-3 pt-3 border-t border-slate-100">
                                                    {/* Role details */}
                                                    <div className="flex items-start gap-2 text-sm">
                                                        <svg className="w-4 h-4 text-slate-400 mt-0.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 13.255A23.931 23.931 0 0112 15c-3.183 0-6.22-.62-9-1.745M16 6V4a2 2 0 00-2-2h-4a2 2 0 00-2 2v2m4 6h.01M5 20h14a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                                                        </svg>
                                                        <div>
                                                            <span className="text-xs font-bold text-slate-400 uppercase tracking-wide block">Current / Latest Role</span>
                                                            <p className="text-slate-800 font-semibold mt-0.5">
                                                                {filters.search ? (
                                                                    <>
                                                                        <HighlightText text={currentJob.jobTitle || 'Role Not Specified'} highlight={filters.search} />
                                                                        {currentJob.company && (
                                                                            <span className="text-slate-500 font-medium"> at <HighlightText text={currentJob.company} highlight={filters.search} /></span>
                                                                        )}
                                                                    </>
                                                                ) : (
                                                                    <>
                                                                        {currentJob.jobTitle || 'Role Not Specified'}
                                                                        {currentJob.company && (
                                                                            <span className="text-slate-500 font-medium"> at {currentJob.company}</span>
                                                                        )}
                                                                    </>
                                                                )}
                                                            </p>
                                                        </div>
                                                    </div>

                                                    {/* Academic credentials */}
                                                    <div className="flex items-start gap-2 text-sm">
                                                        <svg className="w-4 h-4 text-slate-400 mt-0.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" />
                                                        </svg>
                                                        <div>
                                                            <span className="text-xs font-bold text-slate-400 uppercase tracking-wide block">Academic Credentials</span>
                                                            <p className="text-slate-800 font-semibold mt-0.5">
                                                                {education.degree ? (
                                                                    <>
                                                                        {filters.education || filters.search ? (
                                                                            <>
                                                                                <HighlightText text={education.degree} highlight={filters.education || filters.search} />
                                                                                {education.fieldOfStudy && (
                                                                                    <span className="text-slate-500 font-medium">, <HighlightText text={education.fieldOfStudy} highlight={filters.education || filters.search} /></span>
                                                                                )}
                                                                            </>
                                                                        ) : (
                                                                            <>
                                                                                {education.degree}
                                                                                {education.fieldOfStudy && (
                                                                                    <span className="text-slate-500 font-medium">, {education.fieldOfStudy}</span>
                                                                                )}
                                                                            </>
                                                                        )}
                                                                        {education.institution && (
                                                                            <span className="text-slate-400 font-medium text-xs block mt-0.5">{education.institution}</span>
                                                                        )}
                                                                    </>
                                                                ) : 'Not specified'}
                                                            </p>
                                                        </div>
                                                    </div>

                                                    {/* Skills list */}
                                                    {skills.length > 0 && (
                                                        <div className="flex items-start gap-2 text-sm pt-2">
                                                            <svg className="w-4 h-4 text-slate-400 mt-0.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
                                                            </svg>
                                                            <div>
                                                                <span className="text-xs font-bold text-slate-400 uppercase tracking-wide block mb-1.5">Skills</span>
                                                                <div className="flex flex-wrap gap-1.5">
                                                                    {skills.slice(0, 8).map((skill, i) => (
                                                                        <span key={i} className="text-xs px-2.5 py-1 bg-slate-50 text-slate-700 rounded-lg border border-slate-100 font-medium">
                                                                            {filters.skills || filters.search ? (
                                                                                <HighlightText text={skill} highlight={filters.skills || filters.search} />
                                                                            ) : (
                                                                                skill
                                                                            )}
                                                                        </span>
                                                                    ))}
                                                                    {skills.length > 8 && (
                                                                        <span className="text-xs text-[#0e3a61] font-bold self-center px-1.5">+{skills.length - 8} more</span>
                                                                    )}
                                                                </div>
                                                            </div>
                                                        </div>
                                                    )}
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                );
                            })}
                        </div>

                        {/* Enhanced Pagination Controls */}
                        {totalPages > 1 && filteredSeekers.length > 0 && (
                            <div className="mt-8 flex justify-center pb-8">
                                <div className="bg-white rounded-2xl shadow-lg border border-slate-100 p-2 flex items-center gap-2">
                                    <button
                                        onClick={() => handlePageChange(currentPage - 1)}
                                        disabled={currentPage === 1}
                                        className={`px-4 py-2 rounded-xl font-bold text-xs transition-all duration-200 ${currentPage === 1 ? 'text-slate-300 cursor-not-allowed bg-slate-50' : 'text-slate-600 hover:bg-slate-50 hover:text-slate-900'}`}
                                    >
                                        Previous
                                    </button>

                                    <div className="flex items-center gap-1">
                                        {[...Array(totalPages)].map((_, i) => (
                                            (i + 1 === 1 || i + 1 === totalPages || (i + 1 >= currentPage - 1 && i + 1 <= currentPage + 1)) ? (
                                                <button
                                                    key={i}
                                                    onClick={() => handlePageChange(i + 1)}
                                                    className={`w-10 h-10 rounded-xl font-bold text-xs transition-all duration-200 ${currentPage === i + 1 ? 'bg-[#0e3a61] text-white shadow-md' : 'text-slate-600 hover:bg-slate-50 hover:text-slate-900'}`}
                                                >
                                                    {i + 1}
                                                </button>
                                            ) : (i + 1 === currentPage - 2 || i + 1 === currentPage + 2) ? <span key={i} className="px-2 text-slate-400 font-black">...</span> : null
                                        ))}
                                    </div>

                                    <button
                                        onClick={() => handlePageChange(currentPage + 1)}
                                        disabled={currentPage === totalPages}
                                        className={`px-4 py-2 rounded-xl font-bold text-xs transition-all duration-200 ${currentPage === totalPages ? 'text-slate-300 cursor-not-allowed bg-slate-50' : 'text-slate-600 hover:bg-slate-50 hover:text-slate-900'}`}
                                    >
                                        Next
                                    </button>
                                </div>
                            </div>
                        )}

                        {/* Empty State */}
                        {filteredSeekers.length === 0 && (
                            <div className="text-center py-20 bg-white rounded-[2rem] shadow-xl border border-slate-100">
                                <div className="w-20 h-20 bg-slate-50 rounded-[2rem] flex items-center justify-center mx-auto mb-6 border border-slate-100 border-dashed">
                                    <svg className="w-10 h-10 text-slate-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                                    </svg>
                                </div>
                                <h3 className="text-base font-bold text-slate-900 mb-2">No profiles discovered</h3>
                                <p className="text-xs text-slate-500 mb-6 max-w-sm mx-auto">Refine your filters or search keywords to identify matching candidate profiles.</p>
                                <button
                                    onClick={handleClearFilters}
                                    className="px-6 py-3 bg-[#0e3a61] hover:bg-[#0b2e4e] text-white font-bold text-xs rounded-xl shadow-md transition-all duration-200"
                                >
                                    Reset All Filters
                                </button>
                            </div>
                        )}
                    </div>
                </div>
            </div>

            {/* Mobile Sidebar Drawer */}
            {showMobileFilters && (
                <div className="fixed inset-0 z-50 md:hidden">
                    <div className="absolute inset-0 bg-slate-900 bg-opacity-50 backdrop-blur-sm" onClick={() => setShowMobileFilters(false)}></div>
                    <div className="absolute right-0 top-0 bottom-0 w-80 bg-white p-6 overflow-y-auto shadow-2xl animate-slideLeft border-l border-slate-100 rounded-l-[2rem]">
                        <div className="flex justify-between items-center mb-6">
                            <h2 className="text-sm font-extrabold text-slate-900 uppercase tracking-widest">Filters</h2>
                            <button onClick={() => setShowMobileFilters(false)} className="text-slate-500 hover:text-slate-700 transition-colors">
                                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg>
                            </button>
                        </div>
                        <FilterSidebar filters={filters} updateFilter={updateFilter} onClearFilters={handleClearFilters} />
                    </div>
                </div>
            )}
        </div>
    );
};

export default SeekerDatabase;
